import { useTranslate } from '@tolgee/react';
import { GetStaticProps } from 'next';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import React from 'react';
import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import Container from '@amf/shared/components/layout/Container';
import ContactSend from '@amf/shared/components/contact/ContactSend';

import Layout from 'components/layout/Layout';
import Meta from 'components/Meta';

interface ContactPageProps extends NavigationProps {}

export default function ContactPage({ navigation, partners }: ContactPageProps) {
  const { t } = useTranslate();
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head',
      }}
    >
      <div className='ContactPage'>
        <Layout navigation={navigation} partners={partners}>
          <Meta title={t('contact.title')} />
          <Container>
            <h1>{t('contact.title')}</h1>
            <div className='ContactPage__content'>
              <ContactSend buttonProps={{ variant: 'dark' }} />
              <div className='ContactPage__info'>
                <div className='ContactPage__infoPanel'>
                  <h2>{t('contact.info.title')}</h2>
                  <address>
                    Malá kopaná Jihlava zapsaný spolek
                    <br />
                    Palackého 50, 586 01 Jihlava
                  </address>
                  <p>
                    <a href='mailto:info@mfjihlava.cz'>info@mfjihlava.cz</a>
                    <br />
                    <a href='tel:++420728063835'>+420 728 063 835</a>
                  </p>
                </div>
                <div className='ContactPage__infoPanel'>
                  <h2>{t('contact.billing.title')}</h2>
                  <address>
                    <strong>Malá kopaná Jihlava zapsaný spolek</strong>
                    <br />
                    Palackého 50
                    <br />
                    586 01 Jihlava
                  </address>
                  <p>IČ: 48460729</p>
                  <p>{t('contact.billing.account')}: 205073019/0600</p>
                </div>
                <div className='ContactPage__infoPanel'>
                  <h2>{t('contact.social.title')}</h2>
                  <p>
                    <a
                      href='https://www.facebook.com/MalaKopanaJihlava'
                      target='_blank'
                      rel='noreferrer'
                    >
                      Facebook
                    </a>
                    <br />
                    <a href='https://www.instagram.com/mkjihlava/' target='_blank' rel='noreferrer'>
                      Instagram
                    </a>
                    <br />
                    <a
                      href='https://www.youtube.com/channel/UCe9yChaeUenVL6NtK1_Jf_A'
                      target='_blank'
                      rel='noreferrer'
                    >
                      Youtube
                    </a>
                  </p>
                </div>
              </div>
            </div>
            <h2 className='h1'>{t('contact.persons.title')}</h2>
            <div className='ContactPage__personList'>
              <div className='ContactPage__person'>
                <div className='ContactPage__personPhoto ContactPage__personPhoto--lapesOndrej' />
                <div className='ContactPage__personInfo'>
                  <h3>Lapeš Ondřej</h3>
                  <p>
                    Prezident MK Jihlava
                    <br />
                    <br />
                    <a href='tel:+420728063835'>+420 728 063 835</a>
                    <br />
                    <a href='mailto:ondrej.lapes@mfjihlava.cz'>ondrej.lapes@mfjihlava.cz</a>
                  </p>
                </div>
              </div>
              <div className='ContactPage__person'>
                <div className='ContactPage__personPhoto ContactPage__personPhoto--dinnebierLadislav' />
                <div className='ContactPage__personInfo'>
                  <h3>Ladislav Dinnebier</h3>
                  <p>
                    Viceprezident MK Jihlava
                    <br />
                    <br />
                    <a href='tel:+420723287773'>+420 723 287 773</a>
                    <br />
                    <a href='mailto:dinnebier@volny.cz'>dinnebier@volny.cz</a>
                  </p>
                </div>
              </div>
              <div className='ContactPage__person'>
                <div className='ContactPage__personPhoto ContactPage__personPhoto--lapesovaSimona' />
                <div className='ContactPage__personInfo'>
                  <h3>Lapešová Simona</h3>
                  <p>
                    Účetní MK Jihlava
                    <br />
                    <br />
                    <a href='tel:+420728042685'>+420 728 042 685</a>
                  </p>
                </div>
              </div>
              <div className='ContactPage__person'>
                <div className='ContactPage__personPhoto ContactPage__personPhoto--bartuLumir' />
                <div className='ContactPage__personInfo'>
                  <h3>Bartů Lumír</h3>
                  <p>
                    Předseda disciplinární komise
                    <br />
                    <br />
                    <a href='tel:+420606140675'>+420 606 140 675</a>
                  </p>
                </div>
              </div>
              <div className='ContactPage__person'>
                <div className='ContactPage__personPhoto ContactPage__personPhoto--filsakJan' />
                <div className='ContactPage__personInfo'>
                  <h3>Filsak Jan</h3>
                  <p>
                    Předseda kontrolní komise
                    <br />
                    <br />
                    <a href='tel:+420721502494'>+420 721 502 494</a>
                  </p>
                </div>
              </div>
              <div className='ContactPage__person'>
                <div className='ContactPage__personPhoto ContactPage__personPhoto--krivanPavel' />
                <div className='ContactPage__personInfo'>
                  <h3>Křivan Pavel</h3>
                  <p>
                    PR manager
                    <br />
                    <br />
                    <a href='tel:+420605882021'>+420 605 882 021</a>
                  </p>
                </div>
              </div>
            </div>
          </Container>
        </Layout>
      </div>
    </GoogleReCaptchaProvider>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      navigation: await getNavigation(getLanguageLocale(locale!)),
      partners: await getPartners(),
    },
    revalidate: 10,
  };
};
